import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector, store } from '../../store';
import { rehydrateAuth, retryAuthValidation, resetRetryCount } from '../../store/authSlice';
import { showNotificationAlert, fetchNotificationPreferences } from '../../store/notificationSlice';
import { receiveMessage, handleGroupUpdate, updateMessageAcknowledgment, handleBulkAcknowledgments } from '../../store/messagesSlice';
import { invalidateFeatureFlags } from '../../hooks/useFeatureEnabled';
import WebSocketService from '../../services/websocket';
import notificationService from '../../services/notification';
import tokenRefreshService from '../../services/tokenRefresh';
import { AuthService } from '../../services/auth';
import NetworkDetection from '../../utils/networkDetection';
import { Message } from '../../types/message';

interface AuthInitializerProps {
  children: React.ReactNode;
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, validationState, retryCount, networkError, loading, hospital } = useAppSelector(state => state.auth);
  const { preferences } = useAppSelector(state => state.notifications);
  const activeConversation = useAppSelector(state => state.messages.activeConversation);
  const [isOnline, setIsOnline] = useState(NetworkDetection.isOnline());
  const [retryTimer, setRetryTimer] = useState<NodeJS.Timeout | null>(null);
  const activeConversationRef = useRef<string | null>(null);
  const recentlyDispatchedIdsRef = useRef<Map<number, number>>(new Map());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFeatureIdentityRef = useRef<string | null>(null);

  // Handle network state changes
  const handleNetworkChange = useCallback((online: boolean) => {
    setIsOnline(online);
    
    if (online) {
      // Clear offline mode and reset retry count when back online
      if (AuthService.getAuthenticationState().isOffline) {
        dispatch(resetRetryCount());
      }
      
      // If we had validation failures due to network issues, retry now
      if (validationState === 'failed' && networkError && user && NetworkDetection.shouldRetryOnConnection()) {
        dispatch(rehydrateAuth({ forceServerValidation: true }));
      }
    }
  }, [dispatch, validationState, networkError, user]);

  const getConversationIdForMessage = useCallback((incomingMessage: Message): string | null => {
    if (!incomingMessage) {
      return null;
    }

    const explicitConversationId =
      (incomingMessage as any)._conversationId ??
      (incomingMessage as any).conversation_id ??
      (incomingMessage as any).conversationId;

    if (explicitConversationId !== undefined && explicitConversationId !== null) {
      return explicitConversationId.toString();
    }

    const groupIdentifier =
      incomingMessage.group?.id ??
      incomingMessage.group_id ??
      (incomingMessage as any).groupId ??
      (incomingMessage as any).groupID;
    if (groupIdentifier !== undefined && groupIdentifier !== null) {
      return `group_${groupIdentifier}`;
    }

    const senderId = incomingMessage.sender?.id;
    if (!senderId) {
      return null;
    }

    if (user && senderId === user.id) {
      const receiverId =
        incomingMessage.receiver?.id ??
        (incomingMessage as any).receiver_id ??
        (incomingMessage as any).receiverId;
      return receiverId !== undefined && receiverId !== null ? receiverId.toString() : null;
    }

    return senderId.toString();
  }, [user?.id]);

  useEffect(() => {
    activeConversationRef.current = activeConversation || null;
  }, [activeConversation]);

  // Set up network connectivity detection
  useEffect(() => {
    NetworkDetection.addListener(handleNetworkChange);
    const cleanup = NetworkDetection.initialize();
    
    return () => {
      NetworkDetection.removeListener(handleNetworkChange);
      cleanup();
    };
  }, [handleNetworkChange]);

  // Enhanced authentication initialization with retry logic
  useEffect(() => {
    // Clear any existing retry timer
    if (retryTimer) {
      clearTimeout(retryTimer);
      setRetryTimer(null);
    }

    // Always confirm cached auth with the server before protected startup flows run.
    const authState = AuthService.getAuthenticationState();

    if (authState.isAuthenticated && isOnline) {
      // Only force a server re-validation when we don't already hold a
      // confirmed-valid session. Mobile resume commonly fires an `online`
      // event, which re-runs this effect; without this guard every such blip
      // re-validated, flipped `loading`, and refetched the whole page. When the
      // session is already 'valid' there is nothing to confirm — leave it be.
      // Genuine offline->online recovery from a FAILED validation is handled
      // separately by handleNetworkChange (which only retries when a prior
      // validation actually failed).
      const currentValidation = store.getState().auth.validationState;
      if (currentValidation !== 'valid') {
        dispatch(rehydrateAuth({ forceServerValidation: true }));
      }
    } else if (!authState.isAuthenticated && !authState.isOffline) {
      // No existing auth data, attempt rehydration anyway (might have user data)
      dispatch(rehydrateAuth());
    } else {
    }
  }, [dispatch, isOnline]);

  // Handle retry logic for failed validations
  useEffect(() => {
    if (validationState === 'failed' && retryCount > 0 && retryCount < 3 && isOnline && !loading) {
      const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 8000);
      
      
      const timer = setTimeout(() => {
        dispatch(retryAuthValidation());
      }, delay);
      
      setRetryTimer(timer);
      
      return () => {
        clearTimeout(timer);
        setRetryTimer(null);
      };
    }
  }, [validationState, retryCount, isOnline, loading, dispatch]);

  // Reset shared feature-flag cache whenever a validated auth identity is established.
  useEffect(() => {
    const identityKey =
      isAuthenticated && user && validationState === 'valid'
        ? `${user.id}:${hospital?.id ?? 'none'}`
        : null;

    if (identityKey && identityKey !== lastFeatureIdentityRef.current) {
      invalidateFeatureFlags(true);
    }

    lastFeatureIdentityRef.current = identityKey;
  }, [isAuthenticated, validationState, user?.id, hospital?.id]);

  // Set up global WebSocket connection when user is authenticated
  useEffect(() => {
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[DEBUG-AuthInit] useEffect triggered - checking auth state:', {
        isAuthenticated,
        hasUser: !!user,
        userId: user?.id,
        validationState
      });
    }

    // Only set up WebSocket after the current session is confirmed with the backend.
    // Note: Do NOT require document.cookie to contain refresh token - it may be HttpOnly
    // (invisible to JS). Token request uses credentials: 'include' so browser sends cookies.
    const hasRefreshTokenVisible = document.cookie.includes('hospital_refresh_token');
    const isSessionValidated = validationState === 'valid';
    if (isAuthenticated && user && isSessionValidated) {
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[WebSocket] AuthInitializer: starting connect (authenticated, user present, validation valid)');
        console.log('[DEBUG-AuthInit] Auth check passed! Setting up WebSocket for user:', user.id, 'validationState:', validationState);
      }

      // Start automatic token refresh for existing authenticated users (prevents duplicates)
      tokenRefreshService.startAutoRefresh();

      // Clear any existing handlers first to prevent duplicates
      WebSocketService.clearHandlers();

      // Notification permission will be requested when user enables notifications in settings

      // Load user notification preferences
      dispatch(fetchNotificationPreferences());

      // Reset reconnection attempts before connecting (important for page refresh)
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[DEBUG-AuthInit] Step 1: Resetting reconnection attempts');
      }
      WebSocketService.resetReconnectAttempts();

      // Initialize WebSocket connection
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[DEBUG-AuthInit] Step 2: Calling WebSocketService.connect()');
      }
      WebSocketService.resetDisconnectFlag();
      WebSocketService.connect();

      // Set up WebSocket connection status handler with auto-reconnect
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[DEBUG-AuthInit] Step 3: Registering onConnection handler');
      }
      const unsubscribe = WebSocketService.onConnection((connected: boolean) => {
        if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
          console.log('[DEBUG-AuthInit] onConnection handler called! connected=', connected, 'timestamp=', Date.now());
        }

        // Auto-reconnect if disconnected (handles page refresh and connection loss)
        if (!connected && isAuthenticated && user) {
          if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
            console.log('[DEBUG-AuthInit] Disconnected, will attempt reconnect in 2 seconds');
          }

          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }

          // Reconnect after a short delay to avoid rapid reconnection attempts
          reconnectTimeoutRef.current = setTimeout(() => {
            if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
              console.log('[DEBUG-AuthInit] Executing delayed reconnect logic');
            }
            reconnectTimeoutRef.current = null;

            // Use latest state to avoid stale reconnect attempts after logout
            const latestAuth = store.getState().auth;
            const canReconnect =
              latestAuth.isAuthenticated &&
              !!latestAuth.user &&
              latestAuth.validationState === 'valid';

            if (!WebSocketService.isConnected() && canReconnect) {
              if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
                console.log('[DEBUG-AuthInit] Reconnecting now...');
              }
              // Reset reconnection attempts counter and disconnect flag before reconnecting
              WebSocketService.resetReconnectAttempts();
              WebSocketService.resetDisconnectFlag();
              WebSocketService.connect();
            } else {
              if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
                console.log('[DEBUG-AuthInit] Reconnect skipped - already connected or not authenticated');
              }
            }
          }, 2000);
        }
      });
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[DEBUG-AuthInit] Step 4: onConnection handler registered, unsubscribe function created');
      }
      
      // Set up WebSocket message handler for global notifications
      WebSocketService.onMessage((message: Message) => {
        // Use store.getState() so handler always has current user (avoids stale closure / race)
        const currentUser = user ?? store.getState().auth.user;
        if (!currentUser?.id) {
          if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
            console.log('[AuthInitializer] Skipping WebSocket message - no current user', { messageId: message.id });
          }
          return;
        }

        // Check if this is a message confirmation (sender feedback)
        const isConfirmation = (message as any)._isConfirmation;
        const source = isConfirmation ? 'api' : 'websocket';
        const traceId = (message as any)._traceId as string | undefined;

        // Log WebSocket message receipt with tracing
        // Use dynamic import to avoid potential circular dependencies
        const logWebSocketMessage = async () => {
          try {
            const MessageTracer = (await import('../../utils/messageTracer')).default;
            MessageTracer.logStep(traceId || '', 'ws-message-received', {
              messageId: message.id,
              isConfirmation,
              source,
              senderId: message.sender?.id,
              receiverId: message.receiver?.id,
              content: message.content?.substring(0, 50)
            }, 'Frontend');
            
            if (message.id) {
              MessageTracer.associateMessageId(traceId || '', message.id);
            }
          } catch (e) {
            if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
              console.log('[AuthInitializer] WebSocket message received:', {
                messageId: message.id,
                isConfirmation,
                source,
                traceId
              });
            }
          }
        };
        logWebSocketMessage();

        // Always calculate the conversation ID before dispatching
        const baseConversationId = getConversationIdForMessage(message);
        const shouldFallbackToActive = currentUser && message.sender?.id === currentUser.id;
        const resolvedConversationId =
          baseConversationId ?? (shouldFallbackToActive ? activeConversationRef.current : null);
        const enrichedMessage =
          resolvedConversationId && !(message as any)._conversationId
            ? { ...message, _conversationId: resolvedConversationId, _traceId: traceId }
            : { ...message, _traceId: traceId };

        // Deduplicate: skip dispatch if we already dispatched this message id recently (same confirmation delivered multiple times)
        const DISPATCH_DEDUP_MS = 10000;
        const messageIdNum = typeof message.id === 'number' ? message.id : Number(message.id);
        if (!Number.isNaN(messageIdNum)) {
          const now = Date.now();
          const map = recentlyDispatchedIdsRef.current;
          for (const [id, ts] of Array.from(map.entries())) {
            if (now - ts > DISPATCH_DEDUP_MS) map.delete(id);
          }
          if (map.has(messageIdNum)) {
            return;
          }
          map.set(messageIdNum, now);
        }

        // Update message state with appropriate source (recipient and sender both get real-time update)
        dispatch(receiveMessage({ message: enrichedMessage, currentUserId: currentUser.id, source }));

        // Show global notification alert if message is from another user
        // Skip notifications for confirmations (sender feedback only)
        
        if (message.sender?.id !== currentUser.id && !isConfirmation) {
          const conversationIdForMessage = resolvedConversationId ?? getConversationIdForMessage(message);
          
          // Enhanced check: Suppress notification if viewing the same conversation
          // Check both string and number formats for conversation ID matching
          const normalizeConversationId = (id: string | null | undefined): string | null => {
            if (!id) return null;
            return String(id);
          };
          
          const activeConvId = normalizeConversationId(activeConversationRef.current);
          const messageConvId = normalizeConversationId(conversationIdForMessage);
          
          // Check if user is viewing this conversation
          // Only suppress if the active conversation ID matches the message's conversation ID
          const isViewingConversation = activeConvId && messageConvId && activeConvId === messageConvId;
          
          // Check if user is currently on the conversations view of the communicator
          const location = window.location;
          const isOnMessagesPage =
            location.pathname === '/onecare/messages' ||
            location.pathname === '/onecare/messages/';
          
          console.log('[AuthInitializer] 🔔 Notification check:', {
            messageId: message.id,
            senderId: message.sender?.id,
            userId: currentUser.id,
            isConfirmation,
            conversationIdForMessage: messageConvId,
            activeConversation: activeConvId,
            isViewingConversation,
            isOnMessagesPage,
            pathname: location.pathname
          });
          
          // Only suppress notifications if:
          // 1. User is on the messages page AND
          // 2. User is actively viewing THIS specific conversation
          // If user is not on messages page, always show notifications (even if activeConversation is set)
          if (isOnMessagesPage && isViewingConversation) {
            console.log('[AuthInitializer] Skipping notification - user is on messages page and viewing this conversation:', {
              conversationId: messageConvId,
              activeConversation: activeConvId,
              isOnMessagesPage
            });
            return;
          }

          const groupId = message.group?.id ?? message.group_id;
          const isGroupMessage = groupId !== undefined && groupId !== null;
          const senderName = `${message.sender.first_name} ${message.sender.last_name}`.trim() || message.sender.username || 'Unknown';
          
          // Skip creating notification alerts for urgent/critical messages
          // They are already handled by the WebSocket service to prevent duplicates
          const isUrgentOrCritical = message.urgency_level === 'urgent' || message.urgency_level === 'critical';
          
          console.log('[AuthInitializer] 🔔 Creating notification:', {
            messageId: message.id,
            isUrgentOrCritical,
            urgencyLevel: message.urgency_level,
            senderName,
            isGroupMessage
          });
          
          if (isUrgentOrCritical) {
            console.log('[AuthInitializer] Skipping notification - urgent/critical handled by WebSocket service');
          } else {
            const notificationAlert = {
              id: `msg_${message.id}`, // Use only message ID to prevent duplicates
              type: isGroupMessage ? 'group_message' as const : 'message' as const,
              title: isGroupMessage ? `${message.group?.name || 'Group Chat'}` : `New message from ${senderName}`,
              message: message.content,
              senderName,
              conversationId: conversationIdForMessage || (isGroupMessage ? `group_${groupId}` : message.sender.id.toString()),
              groupId: groupId,
              timestamp: new Date(message.timestamp).getTime(),
              urgencyLevel: message.urgency_level,
              urgencyReason: message.urgency_reason
            };
            
            console.log('[AuthInitializer] ✅ Dispatching showNotificationAlert:', notificationAlert);
            // Show in-app notification only for non-urgent/critical messages
            dispatch(showNotificationAlert(notificationAlert));
          }
          
          // Show browser notification if user has enabled desktop notifications
          // Will only show when page is not visible (handled by notification service)
          const shouldShowBrowserNotification = preferences?.enable_desktop_notifications;
          
          if (shouldShowBrowserNotification) {
            if (isGroupMessage) {
              notificationService.showGroupMessageNotification(
                message.group?.name || 'Group Chat',
                senderName,
                message.content,
                groupId
              );
            } else {
              notificationService.showMessageNotification(
                senderName,
                message.content,
                message.sender.id.toString()
              );
            }
          }
        }
      });
      
      // Set up WebSocket group update handler
      WebSocketService.onGroupUpdate((data: { action: string; group: any; details?: any }) => {
        console.log('[DEBUG-AuthInitializer] 📢 Group update received:', data);
        console.log('[DEBUG-AuthInitializer] Dispatching handleGroupUpdate to Redux');
        dispatch(handleGroupUpdate(data));
        console.log('[DEBUG-AuthInitializer] ✅ Dispatch complete');
      });
      
      // Set up WebSocket acknowledgment handlers
      WebSocketService.onAcknowledgment((data: any) => {
        
        if (data.type === 'acknowledgment' && data.acknowledgment) {
          // Single acknowledgment update
          
          dispatch(updateMessageAcknowledgment({
            messageId: data.acknowledgment.message_id,
            acknowledgment: data.acknowledgment
          }));
        } else if (data.type === 'bulk_acknowledgments' && data.acknowledgments) {
          // Bulk acknowledgment updates
          dispatch(handleBulkAcknowledgments({
            acknowledgments: data.acknowledgments
          }));
        } else if (data.type === 'acknowledgment_deleted') {
          // Handle acknowledgment deletion - revert status
          if (data.data && data.data.message_id) {
            // For deletion, we need to determine what status to revert to
            // For now, revert to 'delivered' status
            dispatch(updateMessageAcknowledgment({
              messageId: data.data.message_id,
              acknowledgment: {
                is_read: false,
                is_delivered: true,
                read_at: null
              }
            }));
          }
        }
      });
      
      // Set up periodic connection check (every 30 seconds)
      const interval = setInterval(() => {
        if (isAuthenticated && user && validationState === 'valid' && !WebSocketService.isConnected()) {
          WebSocketService.resetReconnectAttempts();
          WebSocketService.resetDisconnectFlag();
          WebSocketService.connect();
        }
      }, 30000); // Check every 30 seconds

      // Reconnect WebSocket when tab becomes visible (handles background tab suspension)
      const handleVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        const latestAuth = store.getState().auth;
        const canReconnect =
          latestAuth.isAuthenticated &&
          !!latestAuth.user &&
          latestAuth.validationState === 'valid';
        if (!WebSocketService.isConnected() && canReconnect) {
          WebSocketService.resetReconnectAttempts();
          WebSocketService.resetDisconnectFlag();
          WebSocketService.connect();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Mobile bfcache restores (e.g. swipe-back navigation) fire `pageshow`
      // with persisted=true, not visibilitychange — reuse the same reconnect
      // logic so a bfcache restore doesn't leave the chat WebSocket dead.
      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          handleVisibilityChange();
        }
      };
      window.addEventListener('pageshow', handlePageShow);

      // Release the socket when the tab is actually frozen (Chrome Page
      // Lifecycle `freeze`, fired a few seconds after the tab is hidden) or the
      // page is navigated away / put into the back-forward cache (`pagehide`).
      // Holding an open WebSocket while backgrounded keeps the page INELIGIBLE
      // for Chrome's bfcache and makes Android more likely to DISCARD the tab —
      // which is exactly what turned a brief app-switch into a slow full reload.
      // Use disconnect() (a clean 1000 close) so onclose does not spin a
      // reconnect loop while frozen; the resume handlers above call
      // resetDisconnectFlag()+connect() to bring the socket straight back.
      const handleSuspend: () => void = () => {
        WebSocketService.disconnect();
      };
      document.addEventListener('freeze', handleSuspend);
      window.addEventListener('pagehide', handleSuspend);

      // Cleanup WebSocket on logout or unmount
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pageshow', handlePageShow);
        document.removeEventListener('freeze', handleSuspend);
        window.removeEventListener('pagehide', handleSuspend);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        clearInterval(interval);
        unsubscribe();
        tokenRefreshService.stopAutoRefresh();
        WebSocketService.clearHandlers();
        WebSocketService.disconnect();
      };
    } else {
      console.debug('[WebSocket] AuthInitializer: skipping connect', {
        isAuthenticated,
        hasUser: !!user,
        validationState,
        hasRefreshTokenVisible
      });
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[DEBUG-AuthInit] Skipping WebSocket setup - condition not met:', {
          isAuthenticated,
          hasUser: !!user,
          validationState,
          reason: !isAuthenticated ? 'Not authenticated' : !user ? 'No user object' : 'Validation state not valid'
        });
      }
    }
  }, [dispatch, isAuthenticated, user, validationState]);

  // Cleanup retry timer on unmount
  useEffect(() => {
    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [retryTimer]);

  return <>{children}</>;
};

export default AuthInitializer;
